// Lightweight notification helpers — chime, vibrate, and system Notification.
// All functions degrade gracefully on unsupported browsers / iOS.
//
// Session 27.65 — the actual chime waveforms live in App.js (loud
// 2-3 second sustained tones with a queue so orders never overlap).
// This module now delegates to those shared chime helpers via a
// window bridge so we don't duplicate audio code across modules and
// so the queue is respected across every trigger site.

let _audioCtx = null;

function getAudioCtx() {
  if (typeof window === "undefined") return null;
  if (_audioCtx) return _audioCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  _audioCtx = new Ctx();
  return _audioCtx;
}

/**
 * Play the "order ready" chime — the short (~0.9s) descending swoop
 * that fires on the Staff dashboard when kitchen/bar marks a ticket
 * ready. Deliberately shorter than the kitchen/bar new-order chimes
 * because ready-events fire far more frequently and a long tone
 * every 30 seconds would drown out the room.
 *
 * The actual waveform is defined in App.js `playOrderReadyChime()`
 * (Session 27.65). We bridge to it via `window.__nfcsPlayOrderReadyChime`
 * so this module and any future extension can trigger the queued
 * playback without importing App.js.
 */
export function playOrderReadyChime() {
  try {
    if (typeof window !== "undefined" && typeof window.__nfcsPlayOrderReadyChime === "function") {
      window.__nfcsPlayOrderReadyChime();
      return;
    }
    // Fallback — used if App.js hasn't mounted yet (very early
    // boot). Simple two-tone ding-dong so the event isn't silent.
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") { ctx.resume().catch(() => {}); }
    const now = ctx.currentTime;
    const playTone = (freq, startOffset, duration = 0.32) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + startOffset);
      gain.gain.setValueAtTime(0.0001, now + startOffset);
      gain.gain.exponentialRampToValueAtTime(0.25, now + startOffset + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + startOffset + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + startOffset);
      osc.stop(now + startOffset + duration + 0.05);
    };
    playTone(880, 0);
    playTone(1320, 0.18);
  } catch (_e) {
    /* ignore — sound is best-effort */
  }
}

/**
 * Trigger device vibration. Session 27.65 — bumped from ~440ms to
 * 1200ms per user feedback ("kitchen staff hear the vibration even
 * when they can't hear the tone"). No-op on iOS Safari (does not
 * implement Navigator.vibrate).
 */
export function vibrateOrderReady() {
  try {
    // Session 27.69 — prefer Capacitor Haptics on native shells since
    // navigator.vibrate is silently blocked in some APK WebViews.
    if (typeof window !== "undefined"
        && window.Capacitor
        && typeof window.Capacitor.isNativePlatform === "function"
        && window.Capacitor.isNativePlatform()) {
      import("@capacitor/haptics")
        .then(({ Haptics }) => {
          if (Haptics && typeof Haptics.vibrate === "function") {
            Haptics.vibrate({ duration: 1200 }).catch(() => {});
          }
        })
        .catch(() => {});
      return;
    }
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(1200);
    }
  } catch (_e) {
    /* ignore */
  }
}

/**
 * Show a system-level browser Notification (works in background tabs on
 * supported browsers — Chrome/Edge desktop & Android). Will silently no-op if
 * permission is not granted.
 */
export function showSystemNotification(title, options = {}) {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    new Notification(title, { ...options, icon: options.icon || "/favicon.ico" });
  } catch (_e) {
    /* ignore */
  }
}

/**
 * Politely request Notification permission once.  Resolves to the resulting
 * permission string ("granted" | "denied" | "default").
 */
export async function requestNotificationPermission() {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
    if (Notification.permission === "granted" || Notification.permission === "denied") {
      return Notification.permission;
    }
    return await Notification.requestPermission();
  } catch (_e) {
    return "denied";
  }
}

/**
 * Combined fire-and-forget alert: chime + vibration + system notification.
 */
export function triggerOrderReadyAlert(title, body) {
  playOrderReadyChime();
  vibrateOrderReady();
  showSystemNotification(title, { body, tag: "order-status", renotify: true });
}
